import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook pour gérer le système d'astuces
 * Gère le chargement, masquage et restauration des astuces
 */
export const useTips = (module = null) => {
  const [tips, setTips] = useState([]);
  const [dismissedTips, setDismissedTips] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les astuces pour un module spécifique
  const loadModuleTips = useCallback(async () => {
    if (!module) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/tips/module/${module}`,
        { withCredentials: true }
      );
      setTips(response.data.tips || []);
      setError(null);
    } catch (err) {
      console.error('Error loading tips:', err);
      setError(err.message);
      setTips([]);
    } finally {
      setLoading(false);
    }
  }, [module]);

  // Charger toutes les astuces
  const loadAllTips = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/tips`,
        { withCredentials: true }
      );
      setTips(response.data.tips || []);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error loading all tips:', err);
      setError(err.message);
      setTips([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les astuces masquées (pour la rubrique centrale)
  const loadDismissedTips = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/tips/dismissed`,
        { withCredentials: true }
      );
      setDismissedTips(response.data.tips_by_module || {});
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error loading dismissed tips:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Masquer une astuce
  const dismissTip = useCallback(async (tipId, tipModule) => {
    try {
      await axios.post(
        `${API}/tips/dismiss`,
        { tip_id: tipId, module: tipModule },
        { withCredentials: true }
      );
      
      // Mettre à jour l'état local
      setTips(prev => prev.filter(t => t.id !== tipId));
      
      return true;
    } catch (err) {
      console.error('Error dismissing tip:', err);
      return false;
    }
  }, []);

  // Restaurer une astuce
  const restoreTip = useCallback(async (tipId) => {
    try {
      await axios.post(
        `${API}/tips/restore`,
        { tip_id: tipId },
        { withCredentials: true }
      );
      
      // Recharger les astuces masquées
      await loadDismissedTips();
      
      return true;
    } catch (err) {
      console.error('Error restoring tip:', err);
      return false;
    }
  }, [loadDismissedTips]);

  // Charger au montage si un module est spécifié
  useEffect(() => {
    if (module) {
      loadModuleTips();
    }
  }, [module, loadModuleTips]);

  return {
    tips,
    dismissedTips,
    loading,
    error,
    dismissTip,
    restoreTip,
    loadModuleTips,
    loadAllTips,
    loadDismissedTips,
    refresh: module ? loadModuleTips : loadAllTips
  };
};

export default useTips;
